import React, { useState } from "react";
import { toast } from "sonner";
import { History } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn } from "@/admin/AdminLayout";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fmt = (d: string) => (d ? new Date(d).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" }) : "—");

export function VersionHistory({ res, itemId, onRestored, dirty = false }: { res: string; itemId?: string; onRestored: (doc: any) => void; dirty?: boolean }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);
  const [diff, setDiff] = useState<any>(null);
  if (!itemId) return null;
  const compare = async (v: any) => { setDiff({ v, changes: null }); try { setDiff({ v, changes: (await api.get(`/admin-x/version-compare/${v.id}`)).data.changes }); } catch (e) { toast.error(errMsg(e)); setDiff(null); } };
  const load = async () => { setOpen(true); setRows(null); setDiff(null); try { setRows((await api.get(`/admin-x/versions/${res}/${itemId}`)).data.items); } catch (e) { toast.error(errMsg(e)); setRows([]); } };
  const restore = async (v: any) => {
    if (!window.confirm(`Restore the version saved ${fmt(v.saved_at)}?${dirty ? " Your unsaved edits will be lost." : ""} The current version is kept in history first.`)) return;
    try { const r = await api.post(`/admin-x/versions/${v.id}/restore`); toast.success("Earlier version restored"); setOpen(false); onRestored(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Btn ghost onClick={load} data-testid={`history-button-${res}`}><span className="inline-flex items-center gap-1.5"><History size={13} />History</span></Btn>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogTitle className="font-display text-2xl">Change history</DialogTitle>
          <p className="text-xs text-neutral-500 -mt-2">The last 10 saved versions. Restoring keeps a copy of the current version first, so it can be undone.</p>
          {rows === null ? <p className="text-sm">Loading…</p> : !rows.length ? <p className="text-sm text-neutral-600" data-testid="history-empty">No earlier versions yet — they appear after the next save.</p> : (
            <ul className="divide-y divide-neutral-100">
              {rows.map((v, i) => (
                <li key={v.id} className={`py-3 flex items-center justify-between gap-4 ${diff?.v?.id === v.id ? "bg-amber-50 -mx-2 px-2" : ""}`} data-testid={`history-row-${i}`}>
                  <div className="text-sm">
                    <p>{fmt(v.saved_at)}</p>
                    <p className="text-xs text-neutral-500">{v.saved_by ? `by ${v.saved_by}` : "by unknown"}{v.note ? ` · ${v.note}` : ""}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Btn ghost onClick={() => compare(v)} data-testid={`history-compare-${i}`}>Compare</Btn>
                    <Btn ghost onClick={() => restore(v)} data-testid={`history-restore-${i}`}>Restore</Btn>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {diff && (
            <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "start" })} className="border-t border-neutral-200 pt-4" data-testid="history-compare-panel">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm"><span className="font-medium">Version from {fmt(diff.v.saved_at)}</span> compared with what's live now</p>
                <button onClick={() => setDiff(null)} className="text-xs underline" data-testid="history-compare-close">Close</button>
              </div>
              {diff.changes === null ? <p className="text-sm">Loading…</p> : !diff.changes.length ? <p className="text-sm text-neutral-600" data-testid="history-compare-same">No differences — this version matches what's live.</p> : (
                <ul className="space-y-3" data-testid="history-compare-list">
                  {diff.changes.map((c: any, n: number) => (
                    <li key={n} className="text-xs" data-testid={`history-change-${n}`}>
                      <p className="eyebrow text-[10px] text-neutral-500 mb-1">{c.field}</p>
                      <p className="bg-red-50 text-red-900 px-2 py-1.5 whitespace-pre-wrap break-words"><span className="font-medium mr-1">This version:</span><span className="line-through decoration-red-300">{c.before}</span></p>
                      <p className="bg-emerald-50 text-emerald-900 px-2 py-1.5 mt-1 whitespace-pre-wrap break-words"><span className="font-medium mr-1">Live now:</span>{c.after}</p>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4"><Btn onClick={() => restore(diff.v)} data-testid="history-compare-restore">Restore this version</Btn></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
